import { CreateBusinessData } from '../business';

/**
 * Validation utilities for business forms
 */

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validates email format
 * @param email - Email address to validate
 * @returns True if valid, false otherwise
 */
function validateEmail(email: string): boolean {
  if (!email || email.trim() === '') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates phone number format
 * Accepts formats like: (555) 123-4567, 555-123-4567, 5551234567
 * @param phone - Phone number to validate
 * @returns True if valid, false otherwise
 */
function validatePhone(phone: string): boolean {
  if (!phone || phone.trim() === '') return false;
  // Remove all non-numeric characters and check if we have 10 digits
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length === 10;
}

/**
 * Validates US postal code format
 * Accepts 5-digit or 9-digit (Postal+4) formats
 * @param postalCode - Postal code to validate
 * @returns True if valid, false otherwise
 */
function validatePostalCode(postalCode: string): boolean {
  if (!postalCode || postalCode.trim() === '') return false;
  const postalCodeRegex = /^\d{5}(-\d{4})?$/;
  return postalCodeRegex.test(postalCode);
}

/**
 * Validates that hourly rate is a positive number
 * @param rate - Hourly rate to validate
 * @returns True if valid, false otherwise
 */
function validateHourlyRate(rate: number): boolean {
  return rate > 0 && rate < 1000; // Reasonable upper limit
}

/**
 * Validates that at least one service is selected
 * @param services - Array of selected services
 * @returns True if at least one service is selected
 */
function validateServices(services: string[]): boolean {
  return services.length > 0;
}

/**
 * Validates that at least one day is available
 * @param availability - Availability object
 * @returns True if at least one day is true
 */
function validateAvailability(availability: CreateBusinessData['availability']): boolean {
  return Object.values(availability).some(day => day === true);
}

/**
 * Validates insurance information - all or none must be provided
 * @param provider - Insurance provider name
 * @param policyNumber - Policy number
 * @param expirationDate - Expiration date
 * @returns True if valid (all provided or all empty)
 */
function validateInsurance(
  provider?: string,
  policyNumber?: string,
  expirationDate?: string
): boolean {
  const hasProvider = provider && provider.trim() !== '';
  const hasPolicyNumber = policyNumber && policyNumber.trim() !== '';
  const hasExpiration = expirationDate && expirationDate.trim() !== '';

  // Either all filled or all empty
  const allFilled = hasProvider && hasPolicyNumber && hasExpiration;
  const allEmpty = !hasProvider && !hasPolicyNumber && !hasExpiration;

  return allFilled || allEmpty;
}

/**
 * Validates the entire business form
 * @param data - Form data to validate
 * @returns Array of validation errors (empty if valid)
 */
export function validateBusinessForm(data: CreateBusinessData): ValidationError[] {
  const errors: ValidationError[] = [];

  // Required fields
  if (!data.businessName || data.businessName.trim() === '') {
    errors.push({ field: 'businessName', message: 'Business name is required' });
  }

  if (!data.ownerName || data.ownerName.trim() === '') {
    errors.push({ field: 'ownerName', message: 'Owner name is required' });
  }

  if (!data.email || data.email.trim() === '') {
    errors.push({ field: 'email', message: 'Email is required' });
  } else if (!validateEmail(data.email)) {
    errors.push({ field: 'email', message: 'Invalid email format' });
  }

  if (!data.phone || data.phone.trim() === '') {
    errors.push({ field: 'phone', message: 'Phone number is required' });
  } else if (!validatePhone(data.phone)) {
    errors.push({ field: 'phone', message: 'Invalid phone number format. Use (555) 123-4567 format' });
  }

  // Address validation
  if (!data.address.addressLine1 || data.address.addressLine1.trim() === '') {
    errors.push({ field: 'addressLine1', message: 'Street address is required' });
  }

  if (!data.address.addressCity || data.address.addressCity.trim() === '') {
    errors.push({ field: 'addressCity', message: 'City is required' });
  }

  if (!data.address.addressState || data.address.addressState.trim() === '') {
    errors.push({ field: 'addressState', message: 'State is required' });
  }

  if (!data.address.addressPostalCode || data.address.addressPostalCode.trim() === '') {
    errors.push({ field: 'addressPostalCode', message: 'Postal code is required' });
  } else if (!validatePostalCode(data.address.addressPostalCode)) {
    errors.push({ field: 'addressPostalCode', message: 'Invalid postal code format' });
  }

  // Hourly rate validation
  if (!validateHourlyRate(data.hourlyRate)) {
    errors.push({ field: 'hourlyRate', message: 'Hourly rate must be greater than $0' });
  }

  // Services validation
  if (!validateServices(data.servicesOffered)) {
    errors.push({ field: 'servicesOffered', message: 'Select at least one service' });
  }

  // Availability validation
  if (!validateAvailability(data.availability)) {
    errors.push({ field: 'availability', message: 'Select at least one available day' });
  }

  // Insurance validation (optional but if provided, must be complete)
  if (!validateInsurance(data.insuranceProvider, data.policyNumber, data.expirationDate)) {
    errors.push({ 
      field: 'insurance', 
      message: 'If providing insurance information, all fields (provider, policy number, and expiration date) must be filled' 
    });
  }

  return errors;
}

